import { Combobox } from '@headlessui/react';
import styled, { css } from 'styled-components';

import { Box } from '@/shared/ui/box';
import { Caption } from '@/shared/ui/caption';

import { AutocompleteProps, StyledOptional } from './multiselect.types';

export const optionsCss = css`
	padding: 8px 12px 8px 36px;
	outline: none;
	color: ${({ theme: { option } }) => option.color};
	background-color: ${({ theme: { option } }) => option.background};
	font-weight: 500;
	cursor: pointer;
	transition: ${({ theme: { transition } }) => transition.primary};

	&:hover {
		background-color: ${({ theme: { option } }) => option.hover};
	}
`;

export const Options = styled(Combobox.Options)<Required<Pick<AutocompleteProps<string>, 'variant'>> & StyledOptional>`
	outline: none;
	width: 100%;
	position: absolute;
	top: 100%;
	margin-top: 8px;
	overflow-y: ${({ scroll }) => (scroll ? 'scroll' : 'hidden')};
	min-height: ${({ scroll }) => (scroll ? '230px' : 'hidden')};
	max-height: ${({ scroll }) => (scroll ? '230px' : 'hidden')};
	overflow-x: hidden;
	z-index: ${({ theme: { zIndex } }) => zIndex.menu};
	border-radius: ${({ variant, theme: { input } }) => input[variant].radius};
	background-color: ${({ theme: { option } }) => option.background};
	border: ${({ border, theme: { option } }) => (border ? option.border : 'none')};
`;

export const Option = styled(Combobox.Option)`
	${optionsCss}
`;

export const NoMatchesOption = styled.li`
	${optionsCss}
`;

export const Selected = styled.div<{ selected?: boolean }>`
	position: relative;
	display: flex;
	align-items: center;
	color: ${({ selected, theme: { option } }) => (selected ? option.selectedColor : option.color)};
`;

export const CheckIconWrapper = styled.span`
	position: absolute;
	left: -22px;
	top: 50%;
	transform: translateY(-50%);
	display: flex;
	align-items: center;
`;

export const Label = styled(Caption)`
	margin-bottom: 8px;

	& span {
		margin-left: 2px;
		color: ${({
			theme: {
				colors: { error },
			},
		}) => error};
	}
`;

export const Button = styled(Combobox.Button)<
	Required<Pick<AutocompleteProps<string>, 'variant'>> & StyledOptional & { hasValue?: boolean }
>`
	display: flex;
	align-items: center;
	width: 100%;
	border: none;
	outline: none;
	background: none;
	color: ${({ variant, open, hasValue, theme: { input } }) =>
		open ? input[variant].focus.color : hasValue ? input[variant].filled?.color : input[variant].fontColor};
	white-space: pre-wrap;
	padding-right: 24px;
	min-height: 37px;
	height: 100%;

	&[aria-expanded='true'] {
		border: 1px solid ${({ variant, theme: { input } }) => input[variant].focus.border};
		color: ${({ variant, theme: { input } }) => input[variant].focus.color};
	}

	${({ variant, open }) =>
		css`
			padding: 8px 16px;
			text-align: left;
			cursor: pointer;
			border: 1px solid ${({ theme: { input } }) => (open ? input[variant].focus.border : input[variant].border)};
			background-color: ${({ theme: { input } }) => input[variant].background};
			font-size: 15px;
			border-radius: ${({ theme: { input } }) => input[variant].radius};
		`}
`;

export const Multiselect = styled(Box)<{ label?: string; disabled?: boolean }>`
	width: ${({ width, theme: { autocomplete } }) => width ?? autocomplete.width.l};
	pointer-events: ${({ disabled }) => disabled && 'none'};
	margin-top: ${({ label }) => !label && '23px'};

	@media ${({ theme: { media } }) => media.mobile} {
		width: ${({ theme: { autocomplete } }) => autocomplete.width.full};
	}
`;

export const MultiselectArrow = styled(Box)<{ open: boolean; disabled?: boolean }>`
	right: 18px;
	transform: rotate(${({ open }) => (open ? '180deg' : 0)});
	transition: transform 0.2s ease;

	& svg {
		fill: ${({ theme: { select }, disabled }) => {
			if (disabled) return select.fill.disabled;

			return select.fill.default;
		}};
	}
`;
